import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useContent } from '../../context/ContentContext';
import { BACKEND_URL } from '../../lib/site';
import { devDebug } from '../../lib/log';

const resolveSrc = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

// Grid layout by video count (avoids nested ternaries in JSX).
const gridClass = (count) => {
  if (count === 1) return 'max-w-3xl';
  if (count === 2) return 'md:grid-cols-2';
  return 'md:grid-cols-3';
};

/**
 * Plays only while visible in the viewport — saves mobile data and battery
 * compared to autoplaying every published video on page load.
 */
const InViewVideo = ({ src, title }) => {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const p = v.play();
            if (p && p.catch) p.catch((e) => devDebug('Section video autoplay blocked:', e));
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(v);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      src={src}
      className="w-full aspect-video object-cover"
      muted
      loop
      playsInline
      preload="metadata"
      onLoadedMetadata={(e) => {
        try {
          e.target.playbackRate = 0.7;
        } catch (err) {
          devDebug('Video playbackRate unsupported:', err);
        }
      }}
      aria-label={title || 'EPS construction video'}
    />
  );
};

/**
 * Admin-managed showcase videos for a given landing section.
 * Renders nothing until videos are published in Admin -> Content -> Site Videos.
 */
export const SectionVideos = ({ section }) => {
  const { content } = useContent();
  const videos = (content.videos || []).filter((v) => v.section === section && v.file_url);
  if (videos.length === 0) return null;

  return (
    <div
      className={`mt-12 grid gap-6 ${gridClass(videos.length)}`}
      data-testid={`section-videos-${section}`}
    >
      {videos.map((v, i) => (
        <motion.figure
          key={`${v.file_url}-${i}`}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative rounded-xl overflow-hidden border border-border bg-card shadow-[0_16px_50px_rgba(20,24,27,0.08)]"
          data-testid={`section-video-${section}-${i}`}
        >
          <InViewVideo src={resolveSrc(v.file_url)} title={v.title} />
          {v.title && (
            <figcaption className="absolute bottom-3 left-3 bg-background/85 backdrop-blur-sm px-3 py-1.5 rounded-md font-mono-tech text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
              {v.title}
            </figcaption>
          )}
        </motion.figure>
      ))}
    </div>
  );
};
